import { forwardRef, useState } from "react";

import ControlledCheckbox from "shared/components/inputs/checkbox/BaseCheckbox";
import PropTypes from "prop-types";
import Text from "shared/components/data-display/text/Text";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import { StyledLabel, Wrapper } from "./Checkbox.styles";

export { StyledLabel } from "./Checkbox.styles";

const useCheckboxState = (
    defaultChecked = false,
    defaultIndeterminate = false,
    defaultOnChange = () => void 0
) => {
    const [internalChecked, setInternalChecked] = useState(
        defaultIndeterminate ? false : defaultChecked
    );
    const [internalIndeterminate, setInternalIndeterminate] =
        useState(defaultIndeterminate);

    const internalOnChange = (event) => {
        setInternalChecked(event.target.checked);
        setInternalIndeterminate(false);
        defaultOnChange(event);
    };

    return [internalChecked, internalIndeterminate, internalOnChange];
};

const Checkbox = forwardRef(
    (
        {
            label,
            checked,
            indeterminate,
            defaultChecked,
            defaultIndeterminate,
            onChange,
            disabled,
            title,
            ...rest
        },
        ref
    ) => {
        const isControlled = typeof checked === "boolean";
        const [internalChecked, internalIndeterminate, internalOnChange] =
            useCheckboxState(defaultChecked, defaultIndeterminate, onChange);

        const actualChecked = isControlled ? checked : internalChecked;
        const actualIndeterminate = isControlled
            ? indeterminate
            : internalIndeterminate;
        const actualOnChange = isControlled ? onChange : internalOnChange;

        const CheckboxControl = (
            <ControlledCheckbox
                checked={actualChecked}
                indeterminate={actualIndeterminate}
                onChange={actualOnChange}
                disabled={disabled}
                $expandHitArea={label}
                {...rest}
            />
        );

        return (
            <Tooltip text={title}>
                <Wrapper $disabled={disabled} ref={ref}>
                    {label ? (
                        <StyledLabel
                            spacing="Zero_5"
                            align="center"
                            $disabled={disabled}
                        >
                            {CheckboxControl}
                            <Text
                                variant={
                                    actualChecked ? "Body_2_2" : "Body_2_1"
                                }
                            >
                                {label}
                            </Text>
                        </StyledLabel>
                    ) : (
                        CheckboxControl
                    )}
                </Wrapper>
            </Tooltip>
        );
    }
);

Checkbox.propTypes = {
    label: PropTypes.string,
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    defaultChecked: PropTypes.bool,
    defaultIndeterminate: PropTypes.bool,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    title: PropTypes.string,
};

export default Checkbox;
